import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridToolbarExportContainer,
  GridCsvExportMenuItem,
} from "@mui/x-data-grid";
import { Box, IconButton, Button, Stack, TextField } from "@mui/material";
import FileUploader from "./FileUploader";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import UploadIcon from "@mui/icons-material/Upload";
import RefreshIcon from "@mui/icons-material/Refresh";
import FormDialog from "./crops/FormDialog";
import axios from "../api/axios";
import AppBreadcrumbs from "./AppBreadcrumbs";
import SnackbarAlert from "./SnackbarAlert";
import ConfirmDialog from "./ConfirmDialog";
import ExportButtons from "./ExportButtons";

const AdvancedDataTable = ({
  rows,
  columns,
  exportFileName = "data",
  onImport = null,
  onRowDelete = null,
}) => {
  const [searchText, setSearchText] = useState("");
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formInitialData, setFormInitialData] = useState(null);
  const [rowData, setRowData] = useState(rows || []);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleAddClick = () => {
    setFormInitialData(null);
    setFormDialogOpen(true);
  };

  const handleEditClick = (row) => {
    setFormInitialData(row);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (row) => {
    setRowToDelete(row);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/v1/crops/${rowToDelete.id}/`);
      setRowData((prev) => prev.filter((r) => r.id !== rowToDelete.id));
      showSnackbar("Record deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to delete record", "error");
    } finally {
      setConfirmOpen(false);
      setRowToDelete(null);
    }
  };

  const onAdd = async (data) => {
    try {
      const response = await axios.post("/api/v1/crops/", data);
      return response.data;
    } catch (error) {
      console.error("onAdd error:", error);
      throw error;
    }
  };

  const onRowEdit = async (data) => {
    try {
      const response = await axios.put(`/api/v1/crops/${data.id}/`, data);
      return response.data;
    } catch (error) {
      console.error("Edit error:", error);
      throw error;
    }
  };

  const handleFormSubmit = async (data) => {
    try {
      if (data.id && onRowEdit) {
        const updated = await onRowEdit(data);
        if (!updated || !updated.id) throw new Error("Invalid updated data");

        setRowData((prevRows) =>
          prevRows.map((row) => (row.id === updated.id ? updated : row))
        );
        showSnackbar("Record updated successfully", "success");
      } else if (onAdd) {
        const newData = await onAdd(data);
        if (!newData || !newData.id) {
          console.error("newData is invalid:", newData);
          throw new Error("Failed to get created data from server.");
        }

        setRowData((prevRows) => [newData, ...prevRows]);
        showSnackbar("Record added successfully", "success");
      } else {
        console.log("Form submitted:", data);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      showSnackbar("Failed to save data", "error");
    } finally {
      setFormDialogOpen(false);
    }
  };

  const handleRefresh = async () => {
    try {
      const response = await axios.get("/api/v1/crops/");
      setRowData(response.data);
      showSnackbar("Data refreshed successfully", "success");
    } catch (error) {
      setSnackbarMessage("Failed to refresh data.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    setRowData(rows || []);
  }, [rows]);

  const filteredRows = rowData.filter((row) =>
    Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const enhancedColumns = [
    ...columns,
    (onRowEdit || onRowDelete) && {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 100,
      renderCell: (params) => (
        <>
          {onRowEdit && (
            <IconButton
              color="primary"
              onClick={() => handleEditClick(params.row)}
              size="small"
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          )}
          {onRowDelete && (
            <IconButton
              color="error"
              onClick={() => handleDeleteClick(params.row)}
              size="small"
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          )}
        </>
      ),
    },
  ].filter(Boolean);

  const ExportToolbar = () => (
    <GridToolbarExportContainer>
      <GridCsvExportMenuItem options={{ fileName: exportFileName }} />
    </GridToolbarExportContainer>
  );

  return (
    <Box>
      <AppBreadcrumbs baseLabel="Dashboard" baseLink="/dashboard" />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddClick}
          >
            Add
          </Button>

          {onImport && (
            <FileUploader
              variant="outlined"
              size="small"
              onFileUpload={(file) => {
                onImport(file);
                showSnackbar("File uploaded successfully");
              }}
              accept=".csv, .xlsx"
              label="Import"
              icon={<UploadIcon />}
            />
          )}

          <ExportButtons
            data={rows}
            columns={columns}
            fileName={exportFileName}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Stack>

        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={handleSearch}
          sx={{ minWidth: 250 }}
        />
      </Box>

      {/* Data Table */}
      <div style={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={enhancedColumns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          components={{ Toolbar: ExportToolbar }}
          disableSelectionOnClick
          checkboxSelection
        />
      </div>

      {/* Form Dialog */}
      <FormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        initialData={formInitialData}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        description={
          <>
            Are you sure you want to delete <b>{rowToDelete?.name}</b>?
          </>
        }
      />

      <SnackbarAlert
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AdvancedDataTable;
